import type { ComponentProps } from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const secondaryStyle = "border-button-secondary-border bg-button-secondary text-button-secondary-foreground [&:not(:disabled):not([aria-disabled=true])]:hover:bg-button-secondary-hover [&:not(:disabled):not([aria-disabled=true])]:active:bg-button-secondary-pressed"

const buttonVariants = cva(
  "font-inter text-body font-semibold inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-transparent shadow-none transition-[background-color,color,scale] duration-150 cursor-pointer shrink-0 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-3 focus-visible:ring-offset-background motion-safe:[&:not(:disabled):not([aria-disabled=true])]:active:scale-[0.97] motion-reduce:transition-none disabled:cursor-not-allowed aria-disabled:cursor-not-allowed [&:disabled:not([aria-busy=true])]:bg-transparent [&:disabled:not([aria-busy=true])]:border-transparent [&:disabled:not([aria-busy=true])]:text-button-disabled [&:disabled:not([aria-busy=true])]:font-medium [&[aria-disabled=true]:not([aria-busy=true])]:bg-transparent [&[aria-disabled=true]:not([aria-busy=true])]:border-transparent [&[aria-disabled=true]:not([aria-busy=true])]:text-button-disabled aria-busy:cursor-wait aria-busy:before:size-4 aria-busy:before:shrink-0 aria-busy:before:rounded-full aria-busy:before:border-2 aria-busy:before:border-current aria-busy:before:border-t-transparent aria-busy:before:animate-spin motion-reduce:aria-busy:before:animate-none aria-busy:[&>svg]:hidden",
  {
    variants: {
      variant: {
        default: "bg-button-primary text-white [&:not(:disabled):not([aria-disabled=true])]:hover:bg-button-primary-hover [&:not(:disabled):not([aria-disabled=true])]:active:bg-button-primary-pressed",
        destructive: "bg-button-destructive text-white [&:not(:disabled):not([aria-disabled=true])]:hover:bg-button-destructive-hover [&:not(:disabled):not([aria-disabled=true])]:active:bg-button-destructive-pressed",
        outline: secondaryStyle,
        secondary: secondaryStyle,
        ghost: "bg-transparent text-button-secondary-foreground [&:not(:disabled):not([aria-disabled=true])]:hover:bg-button-secondary-hover [&:not(:disabled):not([aria-disabled=true])]:active:bg-button-secondary-pressed",
      },
      size: {
        default: "h-10 px-4 py-2 [@media(pointer:coarse)]:min-h-11",
        sm: "h-9 px-3 py-2 [@media(pointer:coarse)]:min-h-11",
        icon: "size-10 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11",
        "icon-circle": "size-10 rounded-full [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
}) {
  const resolvedClassName = cn(buttonVariants({ variant, size, className }))
  const slotProps = props as ComponentProps<typeof Slot>
  const hasActionSound = variant === "default" || variant === "destructive"

  if (asChild) {
    return (
      <Slot
        data-slot="button"
        data-variant={variant}
        data-size={size}
        data-cuelume-press={hasActionSound ? "" : undefined}
        data-cuelume-release={hasActionSound ? "" : undefined}
        className={resolvedClassName}
        {...slotProps}
      />
    )
  }

  return (
    <button
      data-slot="button"
      data-variant={variant}
      data-size={size}
      data-cuelume-press={hasActionSound ? "" : undefined}
      data-cuelume-release={hasActionSound ? "" : undefined}
      className={resolvedClassName}
      {...props}
    />
  )
}

export { Button }
